import { createFileRoute } from '@tanstack/react-router';
import type { PaginationState } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useState } from 'react';
import { match } from 'ts-pattern';
import ActionButton from '@/components/Button/ActionButton';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ImportJobListTable, MemberTypeSelectDialog, useImportJobList } from '@/features/people-csv-import';
import type { ImportType } from '@/lib/api/orpc/contracts/people-csv-async-import';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CsvImportListSearchSchema } from './-zod-schema';
const MEMBER_TYPE_TABS: ImportType[] = ['EMPLOYEE', 'BOARD_MEMBER', 'CONTRACTOR', 'COLLABORATOR'];
const CsvImportList = () => {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.csvImport'
  });

  // 動的キー（テンプレートリテラル）を避け、importType ごとに明示キーで解決する
  const memberTypeLabel = (type: ImportType): string => match(type).with('EMPLOYEE', () => t('memberType.employee')).with('BOARD_MEMBER', () => t('memberType.boardMember')).with('CONTRACTOR', () => t('memberType.contractor')).with('COLLABORATOR', () => t('memberType.collaborator')).exhaustive();
  const {
    data,
    isPending
  } = useImportJobList({
    page: search.page,
    pageSize: search.pageSize,
    orderDirection: 'desc',
    importType: search.type
  });
  const pagination: PaginationState = {
    pageIndex: search.page - 1,
    pageSize: search.pageSize
  };
  const handlePaginationChange = useCallback((updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    const current: PaginationState = {
      pageIndex: search.page - 1,
      pageSize: search.pageSize
    };
    const next = typeof updater === 'function' ? updater(current) : updater;
    navigate({
      search: {
        ...search,
        page: next.pageIndex + 1,
        pageSize: next.pageSize
      }
    });
  }, [navigate, search]);
  const handleTabChange = useCallback((value: string) => {
    navigate({
      search: {
        ...search,
        type: value as ImportType,
        page: 1
      }
    });
  }, [navigate, search]);
  const handleSelectJob = useCallback((importId: string) => {
    navigate({
      to: '/person/csv-import/$importId',
      params: {
        importId
      }
    });
  }, [navigate]);
  const handleConfirmType = useCallback((type: ImportType) => {
    setIsDialogOpen(false);
    navigate({
      to: '/person/csv-import/upload',
      search: {
        type
      }
    });
  }, [navigate]);
  return <VStack className="mx-auto w-[1000px] py-10">
      <Text variant="h1">{t('list.title')}</Text>
      <Spacer size={8} />
      <Text variant="p" className="text-neutral-muted-on-fill">
        {t('list.pageDescription')}
      </Text>
      <Spacer size={24} />

      <HStack className="w-full items-center justify-between">
        <Tabs value={search.type} onValueChange={handleTabChange}>
          <TabsList>
            {MEMBER_TYPE_TABS.map(type => <TabsTrigger key={type} value={type}>
                {memberTypeLabel(type)}
              </TabsTrigger>)}
          </TabsList>
        </Tabs>
        <ActionButton variant="default" icon="PlusIcon" onClick={() => setIsDialogOpen(true)}>
          {t('list.uploadButton')}
        </ActionButton>
      </HStack>
      <Spacer size={16} />

      <ImportJobListTable items={data?.items ?? []} isLoading={isPending} totalCount={data?.count ?? 0} pageCount={data?.maxPage ?? 1} pagination={pagination} onPaginationChange={handlePaginationChange} onSelect={handleSelectJob} />

      <MemberTypeSelectDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} onConfirm={handleConfirmType} />
    </VStack>;
};
export const Route = createFileRoute('/_staff/person/csv-import/')({
  validateSearch: zodValidator(CsvImportListSearchSchema),
  component: CsvImportList
});